import { useAuth } from 'react-oidc-context';
import {
  useTenant,
  useAccessRoleCount,
  useResourceServerPermissionCount,
  useResourceServerCount,
  useUmrsRoleCount,
} from '@authifi/clique-react-ui';
import { LoginTemplateSelector } from '@authifi/login-pages';
import { API_BASE_URL, CLIENT_ID } from '../auth-config';

export function DashboardPage() {
  const auth = useAuth();
  const { tenantId } = useTenant();
  const profile = auth.user?.profile;
  const { data: roleCount } = useAccessRoleCount();
  const { data: permissionCount } = useResourceServerPermissionCount();
  const { data: resourceServerCount } = useResourceServerCount();
  const { data: umrsRoleCount } = useUmrsRoleCount();

  return (
    <div>
      <h2 className="text-2xl font-semibold text-[var(--clique-fg)] mb-6">Dashboard</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <DashboardCard title="Groups" icon="pi pi-users" linkTo="/groups" />
        <DashboardCard title="Users" icon="pi pi-user" linkTo="/users" />
        <DashboardCard title="Roles" icon="pi pi-shield" linkTo="/roles" count={roleCount} />
        <DashboardCard
          title="Permissions"
          icon="pi pi-lock"
          linkTo="/permissions"
          count={permissionCount}
        />
        <DashboardCard
          title="Resource Servers"
          icon="pi pi-server"
          linkTo="/resource-servers"
          count={resourceServerCount}
        />
        <DashboardCard
          title="UMRS Roles"
          icon="pi pi-id-card"
          linkTo="/umrs-roles"
          count={umrsRoleCount}
        />
      </div>

      <div className="bg-[var(--clique-bg)] rounded-lg border border-[var(--clique-border)] p-6 mb-8">
        <h3 className="text-lg font-medium text-[var(--clique-fg)] mb-4">Login Template</h3>
        <LoginTemplateSelector previewBaseUrl={API_BASE_URL} clientId={CLIENT_ID} />
      </div>

      <div className="bg-[var(--clique-bg)] rounded-lg border border-[var(--clique-border)] p-6">
        <h3 className="text-lg font-medium text-[var(--clique-fg)] mb-4">Session Info</h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-[var(--clique-fg-muted)]">Name</dt>
            <dd className="text-[var(--clique-fg)] font-medium">{profile?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[var(--clique-fg-muted)]">Email</dt>
            <dd className="text-[var(--clique-fg)] font-medium">{String(profile?.email ?? '—')}</dd>
          </div>
          <div>
            <dt className="text-[var(--clique-fg-muted)]">Tenant</dt>
            <dd className="text-[var(--clique-fg)] font-medium">{tenantId}</dd>
          </div>
          <div>
            <dt className="text-[var(--clique-fg-muted)]">Token Expires</dt>
            <dd className="text-[var(--clique-fg)] font-medium">
              {auth.user?.expires_at
                ? new Date(auth.user.expires_at * 1000).toLocaleTimeString()
                : '—'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function DashboardCard({
  title,
  icon,
  linkTo,
  count,
}: {
  title: string;
  icon: string;
  linkTo: string;
  count?: number;
}) {
  return (
    <a
      href={linkTo}
      className="bg-[var(--clique-bg)] rounded-lg border border-[var(--clique-border)] p-5 hover:shadow-md transition-shadow block"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <i aria-hidden="true" className={`${icon} text-2xl text-[var(--clique-primary)]`} />
          <span className="text-lg font-medium text-[var(--clique-fg)]">{title}</span>
        </div>
        {count !== undefined && (
          <span className="text-sm font-semibold text-[var(--clique-fg-muted)]">{count}</span>
        )}
      </div>
    </a>
  );
}
